import { defineStore } from 'pinia'
import TextCell from '@/onenetwork/components/datatable/TextCell.vue'
import DateTimeCell from '@/onenetwork/components/datatable/DateTimeCell.vue'
import TagCell from '@/onenetwork/components/datatable/TagCell.vue'
import { uuid } from 'vue3-uuid'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import AddressCell from '@/onenetwork/components/datatable/AddressCell.vue'
import ListingSubjectCell from '@/onenetwork/components/datatable/ListingSubjectCell.vue'
import ListingStatisticCell from '@/onenetwork/components/datatable/ListingStatisticCell.vue'
import AccountCell from '@/onenetwork/components/datatable/AccountCell.vue'
import RealestateAPI from '@/modules/realestate/constants/realestate.api.uri.const.ts'
import { buildFilterObject } from '@/utils/filter'
import { formatTableDataListing } from '../helper/table'
import {
  PROCESSING_STATUS,
  LISTING_TYPE_OPTIONS,
  LISTING_PROCESSING_STATUS_OPTIONS,
  REQUEST_TYPE,
  REQUEST_TYPE_OPTIONS,
} from '../constants/realestate.const'
import { markRaw } from 'vue'

export interface tabItem {
  id: string
  label: string
  active: boolean
}

export enum ListingListApprovalActionTypes {
  FILTERING = 'FILTERING',
  PENDING = 'PENDING',
  FETCHING = 'FETCHING',
  FETCH_SUCCESS = 'FETCH_SUCCESS',
  FETCH_FAILURE = 'FETCH_FAILURE',
  REJECTING = 'REJECTING',
  REJECT_SUCCESS = 'REJECT_SUCCESS',
  REJECT_FAILURE = 'REJECT_FAILURE',
  DELETING = 'DELETING',
  DELETE_SUCCESS = 'DELETE_SUCCESS',
  DELETE_FAILURE = 'DELETE_FAILURE',
}

interface ListingListApprovalStore {
  action_type: ListingListApprovalActionTypes
  action_mess: string
  uuid: string
  data: Object
  filter: Array<{
    key: string
    type: string
    label: string
    placeholder: string
    value: any
    options?: Array<{ label: string; value: string }>
  }>
  pageSize: number
  page: number
  tabs: Array<any>
  tableConfig: any
  selectedIds: Array<string | number>
}

export const useListingListApprovalStore = defineStore('listing_approval.list', {
  state: (): ListingListApprovalStore => ({
    action_type: ListingListApprovalActionTypes.PENDING,
    action_mess: '',
    uuid: '',
    data: {
      list: [],
      totalRecords: 0,
      totalPage: 0,
    },
    filter: [
      {
        key: 'request_type',
        type: 'select-multiple',
        label: 'Lọc theo yêu cầu',
        placeholder: 'Chọn yêu cầu',
        value: '',
        options: REQUEST_TYPE_OPTIONS,
      },
      {
        key: 'province_id',
        type: 'region-level-1',
        label: 'Lọc theo tỉnh/thành',
        placeholder: 'Chọn tỉnh/thành',
        value: '',
        options: null,
      },
      {
        key: 'ward_id',
        type: 'region-level-2',
        label: 'Lọc theo phường / xã',
        placeholder: 'Chọn phường / xã',
        value: '',
        options: null,
      },
      {
        key: 'map_sheet_number',
        type: 'text',
        label: 'Số tờ',
        placeholder: 'Nhập số tờ',
        value: '',
      },
      {
        key: 'plot_number',
        type: 'text',
        label: 'Số thửa',
        placeholder: 'Nhập số thửa',
        value: '',
      },
      {
        key: 'keyword',
        type: 'text',
        label: 'Lọc theo tiêu đề',
        placeholder: 'Nhập tiêu đề',
        value: '',
      },
      {
        key: 'address',
        type: 'text',
        label: 'Lọc theo địa chỉ',
        placeholder: 'Nhập địa chỉ',
        value: '',
      },
      {
        key: 'listing_type',
        type: 'select-multiple',
        label: 'Lọc theo loại tin',
        placeholder: 'Chọn phân loại',
        value: '',
        options: LISTING_TYPE_OPTIONS,
      },
      {
        key: 'processing_status',
        type: 'select-multiple',
        label: 'Lọc theo trạng thái',
        placeholder: 'Chọn trạng thái',
        value: '',
        options: LISTING_PROCESSING_STATUS_OPTIONS,
      },
    ],
    pageSize: 10,
    page: 1,
    tabs: [
      {
        id: 'list',
        label: 'Danh sách tin đăng BDS chờ phê duyệt',
        active: true,
      },
    ],
    tableConfig: {
      canShowHide: true,
      columns: {
        code: {
          label: 'Mã tin',
          component: markRaw(TextCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        action: {
          label: 'Loại yêu cầu',
          component: markRaw(TagCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        type: {
          label: 'Loại tin',
          component: markRaw(TagCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        address: {
          label: 'Địa chỉ',
          component: markRaw(AddressCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        subject: {
          label: 'Thông tin',
          component: markRaw(ListingSubjectCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        author: {
          label: 'Đăng bởi',
          component: markRaw(AccountCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        statistic: {
          label: 'Thống kê',
          component: markRaw(ListingStatisticCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        status: {
          label: 'Trạng thái',
          component: markRaw(TagCell),
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        regdate: {
          label: 'Ngày đăng ký',
          component: markRaw(DateTimeCell),
          class: '',
          visible: true,
          sort: '',
          config: {},
        },
      },
    },
    selectedIds: [],
  }),
  getters: {
    sortFilter: (state) => {
      const sort: any = []
      if (Object.keys(state.tableConfig?.columns).length > 0) {
        Object.keys(state.tableConfig?.columns).forEach((col) => {
          if (state.tableConfig.columns[col].sort) {
            sort.push([col, state.tableConfig.columns[col].sort])
          }
        })
      }
      return sort
    },
  },
  actions: {
    //TAB ACTION
    changeTab(targetId: string) {
      for (const tab of this.tabs) {
        tab.active = tab.id === targetId
      }
    },
    closeTab(targetId: string) {
      const index = this.tabs.findIndex((tab) => tab.id === targetId)
      if (index !== -1) {
        this.tabs.splice(index, 1)
      }
    },
    closeAllTab() {
      this.tabs = [
        {
          id: 'list',
          label: 'Danh sách tin đăng BDS chờ phê duyệt',
          active: true,
        },
      ]
    },
    newTab(item: tabItem) {
      const exists = this.tabs.some((tab) => tab.id === item.id)
      if (!exists) {
        this.tabs.push(item)
      }
      this.changeTab(item.id)
    },

    //FILTER ACTION
    setFilter(filter: any) {
      this.filter = filter
      this.action_type = ListingListApprovalActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    changeFilter(key: string, value: any) {
      const filterItem = this.filter.find((item: { key: string }) => item.key === key)
      if (filterItem) {
        filterItem.value = value
        this.action_type = ListingListApprovalActionTypes.FILTERING
        this.uuid = uuid.v4()
      }
    },
    putFilter(key: string, value: any) {
      this.filter = this.filter.map((field) => {
        if (field.key === key) {
          return { ...field, value: value }
        }
        return field
      })
    },

    //PAGE ACTION
    changePage(page: number) {
      this.page = page
      this.action_type = ListingListApprovalActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    changePageSize(size: number) {
      this.pageSize = size
      this.action_type = ListingListApprovalActionTypes.FILTERING
      this.uuid = uuid.v4()
    },

    //TABLE CONFIG ACTION
    toggleColumnVisibility(columnCode: string, visible: boolean) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].visible = visible
      } else {
        console.warn(`Column "${columnCode}" không tồn tại trong tableConfig`)
      }
    },
    toggleSort(columnCode: string, sort: string) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].sort = sort
      } else {
        console.warn(`Column "${columnCode}" không tồn tại trong tableConfig`)
      }
    },

    //DATA ACTION
    async fetchList() {
      return new Promise(async (resolve, reject) => {
        try {
          this.action_type = ListingListApprovalActionTypes.FETCHING
          const sort = this.sortFilter.length > 0 ? this.sortFilter : [['created_at', 'desc']]
          const pagination = {
            page: this.page,
            pageSize: this.pageSize,
            sort,
            has_request_type: true,
            request_type: [REQUEST_TYPE.POST, REQUEST_TYPE.UPDATE, REQUEST_TYPE.DELETE],
          }
          const res = await APIClient.get(RealestateAPI.listing.list, {
            params: buildFilterObject(this.filter, pagination),
          })
          const { data } = res.data
          this.data = {
            list: formatTableDataListing(data.data || []),
            totalRecords: data.total ?? 0,
            totalPage: data.last_page ?? 0,
          }
          this.action_type = ListingListApprovalActionTypes.FETCH_SUCCESS
          resolve(res)
        } catch (e) {
          this.action_type = ListingListApprovalActionTypes.FETCH_FAILURE
          reject(e)
        }
      })
    },

    //BULK REJECT ACTION
    async bulkReject(listing_ids: Array<string | number>) {
      return new Promise(async (resolve, reject) => {
        try {
          this.action_type = ListingListApprovalActionTypes.REJECTING
          const res = await APIClient.put(RealestateAPI.listing.reject, {
            listing_ids,
          })
          this.action_type = ListingListApprovalActionTypes.REJECT_SUCCESS
          resolve(true)
        } catch (e) {
          console.error('Error rejecting listings:', e)
          this.action_type = ListingListApprovalActionTypes.REJECT_FAILURE
          reject(e?.response?.data)
        }
      })
    },

    //BULK DELETE ACTION
    async bulkDelete(listing_ids: Array<string | number>) {
      return new Promise(async (resolve, reject) => {
        try {
          this.action_type = ListingListApprovalActionTypes.DELETING
          const res = await APIClient.delete(RealestateAPI.listing.delete, {
            data: { listing_ids },
          })
          this.action_type = ListingListApprovalActionTypes.DELETE_SUCCESS
          resolve(true)
        } catch (e) {
          console.error('Error deleting listings:', e)
          this.action_type = ListingListApprovalActionTypes.DELETE_FAILURE
          reject(e?.response?.data)
        }
      })
    },
  },
})
